import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Trash2, CheckCircle2, Pencil, Share2, Mail, Bell, ArrowUpToLine, Eye } from "lucide-react";
import { SoldDialog } from "@/components/SoldDialog";
import { FirstSaleCelebration } from "@/components/FirstSaleCelebration";
import { DeleteAccount } from "@/components/DeleteAccount";
import { DownloadData } from "@/components/DownloadData";
import { RelistButton } from "@/components/RelistButton";
import { ProfileForm } from "@/components/ProfileForm";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ListingCard } from "@/components/ListingCard";
import { api, formatErr } from "@/lib/api";
import { useAuth, useRegion } from "@/context/AuthContext";

const searchLink = (s) => {
  const p = new URLSearchParams();
  if (s.q) p.set("q", s.q);
  if (s.category) p.set("category", s.category);
  if (s.free_only) p.set("free", "1");
  if (s.max_price != null) p.set("max_price", s.max_price);
  if (s.radius_km) { p.set("near", s.near); p.set("lat", s.lat); p.set("lng", s.lng); p.set("radius", s.radius_km); }
  return `/browse?${p.toString()}`;
};

function SavedSearches() {
  const { setRegion } = useRegion();
  const [items, setItems] = useState(null);
  const load = () => api.get("/my/searches").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/my/searches/${id}`); toast.success("Saved search deleted"); load(); };

  if (!items) return <p className="text-slate-400">Loading…</p>;
  if (!items.length) return <div data-testid="searches-empty" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">No saved searches yet. Search on Browse, then tap "Save search" to get emailed when new matches are listed.</div>;
  return (
    <ul className="max-w-2xl space-y-3">
      {items.map((s) => (
        <li key={s.id} data-testid={`saved-search-${s.id}`} className="flex items-center gap-4 rounded-2xl bg-[#173742] p-4">
          <Bell className="h-5 w-5 shrink-0 text-[#d4f253]" />
          <Link to={searchLink(s)} onClick={() => s.state && setRegion(s.state)} className="min-w-0 flex-1 truncate font-semibold hover:underline">{s.label}</Link>
          <button data-testid={`saved-search-delete-${s.id}`} onClick={() => del(s.id)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
        </li>
      ))}
    </ul>
  );
}

const PREFS = [
  { key: "email_message_alerts", testId: "settings-email-alerts-switch", title: "Email me when a buyer messages me", hint: "We only email once per conversation until you read it.", on: "Message emails turned on", off: "Message emails turned off" },
  { key: "email_price_alerts", testId: "settings-price-alerts-switch", title: "Price drop alerts on saved items", hint: "Get an email when a seller lowers the price of something you've saved.", on: "Price drop emails turned on", off: "Price drop emails turned off" },
  { key: "email_bump_reminders", testId: "settings-bump-reminders-switch", title: "Weekly bump reminders", hint: "We'll email you when your free weekly bump is ready for your listings.", on: "Bump reminders turned on", off: "Bump reminders turned off" },
];

function Settings() {
  const { user, setUser } = useAuth();
  const toggle = async (p, v) => {
    const { data } = await api.patch("/auth/me/settings", { [p.key]: v });
    setUser(data);
    toast.success(v ? p.on : p.off);
  };
  return (
    <div className="max-w-xl divide-y divide-white/10 rounded-2xl bg-[#173742]">
      <p className="px-6 pt-5 pb-3 text-xs text-slate-400">Emails go to {user.email}</p>
      {PREFS.map((p) => (
        <div key={p.key} className="flex items-start gap-4 p-6">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#d4f253]" />
          <div className="flex-1">
            <p className="font-semibold">{p.title}</p>
            <p className="mt-1 text-sm text-slate-400">{p.hint}</p>
          </div>
          <Switch className="data-[state=unchecked]:bg-slate-500" data-testid={p.testId} checked={user[p.key] !== false} onCheckedChange={(v) => toggle(p, v)} />
        </div>
      ))}
    </div>
  );
}

function Actions({ l, reload }) {
  const [soldOpen, setSoldOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const setStatus = async (status, noShow = false) => {
    try { await api.patch(`/listings/${l.id}/status`, { status, no_show: noShow }); toast.success(noShow ? "Relisted — buyer flagged as a no-show for review" : "Listing re-activated"); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async () => {
    try { await api.delete(`/listings/${l.id}`); toast.success("Listing deleted"); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  const nextBump = new Date(new Date(l.bumped_at || l.created_at).getTime() + 7 * 864e5);
  const canBump = l.status === "active" && nextBump <= new Date();
  const bump = async () => {
    try { await api.post(`/listings/${l.id}/bump`); toast.success("Bumped to the top!"); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="border-t border-slate-100 p-2">
    <p data-testid={`dash-views-${l.id}`} className="mb-1 flex items-center justify-center gap-1 text-xs text-slate-500"><Eye className="h-3.5 w-3.5" />{l.views || 0} {l.views === 1 ? "person has" : "people have"} viewed this</p>
    {l.status === "active" && (
      <button data-testid={`dash-bump-${l.id}`} onClick={bump} disabled={!canBump} title={canBump ? "Move to the top of search results" : ""} className="mb-1 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-semibold text-[#0f2a33] hover:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-transparent">
        <ArrowUpToLine className="h-3.5 w-3.5" />{canBump ? "Bump to top — free" : `Bump again ${nextBump.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`}
      </button>
    )}
    <div className="flex gap-1">
      {["hidden", "removed"].includes(l.status)
        ? <span data-testid={`dash-under-review-${l.id}`} className="flex flex-1 items-center justify-center rounded-lg bg-orange-100 py-2 text-xs font-bold text-orange-700">Under review</span>
        : l.status === "sold"
        ? <RelistButton l={l} onRelist={(noShow) => setStatus("active", noShow)} />
        : <button data-testid={`dash-mark-sold-${l.id}`} onClick={() => setSoldOpen(true)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#d4f253] py-2 text-xs font-bold hover:bg-[#c2e23d]"><CheckCircle2 className="h-3.5 w-3.5" />I sold it</button>}
      <SoldDialog listing={l} open={soldOpen} onOpenChange={setSoldOpen} onDone={(d) => { if (d?.first_sale) setCelebrate(true); reload(); }} />
      <FirstSaleCelebration open={celebrate} onClose={() => setCelebrate(false)} title={l.title} />
      <Link to={`/edit/${l.id}`} data-testid={`dash-edit-${l.id}`} className="grid w-9 place-items-center rounded-lg hover:bg-slate-100"><Pencil className="h-4 w-4" /></Link>
      <AlertDialog>
        <AlertDialogTrigger data-testid={`dash-delete-${l.id}`} className="grid w-9 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this listing?</AlertDialogTitle><AlertDialogDescription>This can't be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid={`dash-delete-confirm-${l.id}`} onClick={del}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </div>
  );
}

const Grid = ({ items, render, empty }) => items === null ? <p className="text-slate-400">Loading…</p> : items.length ? (
  <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">{items.map(render)}</div>
) : <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">{empty}</div>;

export default function Dashboard() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [mine, setMine] = useState(null);
  const [saved, setSaved] = useState(null);
  const load = () => { api.get("/my/listings").then((r) => setMine(r.data)); api.get("/my/saved").then((r) => setSaved(r.data)); };
  useEffect(load, []);
  const sold = mine?.filter((l) => l.status === "sold").length || 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">G'day, {user.name}</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Your dashboard</h1>
          <p data-testid="dash-stats" className="mt-2 text-sm text-slate-400">{mine?.length || 0} listings · {sold} sold</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to={`/seller/${user.id}`} data-testid="dash-public-profile-link" className="flex items-center gap-2 rounded-xl border border-white/15 px-5 py-3 text-sm font-semibold hover:bg-white/5"><Share2 className="h-4 w-4" />Share my profile</Link>
          <Link to="/sell" data-testid="dash-sell-btn" className="btn-lime px-5 py-3 text-sm"><Plus className="h-4 w-4" />New listing</Link>
        </div>
      </div>
      <Tabs defaultValue={sp.get("tab") || "mine"} className="mt-8">
        <TabsList className="h-auto flex-wrap justify-start bg-[#173742]">
          <TabsTrigger value="mine" data-testid="dash-tab-mine">My listings</TabsTrigger>
          <TabsTrigger value="saved" data-testid="dash-tab-saved">Saved items</TabsTrigger>
          <TabsTrigger value="settings" data-testid="dash-tab-settings">Profile &amp; settings</TabsTrigger>
          <TabsTrigger value="searches" data-testid="dash-tab-searches">Saved searches</TabsTrigger>
        </TabsList>
        <TabsContent value="mine" className="mt-6">
          <Grid items={mine} empty="You haven't listed anything yet." render={(l, i) => <ListingCard key={l.id} l={l} i={i} action={<Actions l={l} reload={load} />} />} />
        </TabsContent>
        <TabsContent value="saved" className="mt-6">
          <Grid items={saved} empty="No saved items. Tap the heart on any listing." render={(l, i) => <ListingCard key={l.id} l={l} i={i} />} />
        </TabsContent>
        <TabsContent value="settings" className="mt-6">
          <ProfileForm />
          <Settings />
          <DownloadData />
          <DeleteAccount />
        </TabsContent>
        <TabsContent value="searches" className="mt-6">
          <SavedSearches />
        </TabsContent>
      </Tabs>
    </div>
  );
}
